/**
 *  @class
 *  @function FreeShippingProgressBar
 */

if (!customElements.get('free-shipping-progress-bar')) {
    let currentPercent = 0; // Store the current percentage globally
    class FreeShippingProgressBar extends HTMLElement {
      constructor() {
        super();
        this._wasEligible = undefined;
        this._hasRendered = false;
      }

      connectedCallback() {
        this.updateStatus();
      }

      updateStatus() {
        const remainingAmountText = this.querySelector('.free-shipping__info-remaining strong');
        const cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
        const freeShippingThreshold = Math.round(parseInt(this.dataset.shippingAmount, 10) * (Shopify.currency.rate || 1));
        const remainingInfo = this.querySelector('.free-shipping__info-remaining');
        const eligibilityInfo = this.querySelector('.free-shipping__info-eligibility');
        const fillElement = this.querySelector('.progress-bar-percentage');
        const isEligible = cartTotal >= freeShippingThreshold;
        const remainingAmount = Math.max(freeShippingThreshold - cartTotal, 0);

        if (remainingAmountText && !isEligible) {
          const currencyFormat = window.theme.settings.money_with_currency_format || "${{amount}}";
          remainingAmountText.innerHTML = formatMoney(remainingAmount, currencyFormat);
        }

        if (isEligible) {
          remainingInfo.classList.add('is-hidden');
          eligibilityInfo.classList.remove('is-hidden');
        } else {
          remainingInfo.classList.remove('is-hidden');
          eligibilityInfo.classList.add('is-hidden');
        }

        // Handle eligible state change and fill color
        if (fillElement) {
          if (isEligible && this._wasEligible !== true) {
            fillElement.classList.add('progress-bar-percentage--eligible');
          } else if (!isEligible && this._wasEligible === true) {
            fillElement.classList.remove('progress-bar-percentage--eligible');
          }
        }

        this._wasEligible = isEligible;

        // Expose current state for badge logic
        this.dataset.remaining = remainingAmount;
        this.dataset.eligible = isEligible ? 'true' : 'false';

        const progressPercentage = isEligible ? 1 : cartTotal / freeShippingThreshold;
        this.animateProgressBar(progressPercentage, fillElement);
        this._hasRendered = true;

        // Update addon card pricing and badges after bar is updated
        updateAddonCardPricing();
        updateSuggestionBadges();
      }

      animateProgressBar(progressPercentage, fillElement) {
        const targetPercent = progressPercentage * 100;
        const startPercent = currentPercent;
        const duration = 550; // Match the CSS transition duration
        const startTime = performance.now();

        const ease = (t) => t * (2 - t);

        // Trigger pulse on meaningful progress change (not on first paint)
        const shouldPulse = this._hasRendered && Math.abs(targetPercent - startPercent) > 0.5 && fillElement;
        if (shouldPulse) {
          fillElement.classList.add('is-pulsing');
          const handleAnimationEnd = () => {
            fillElement.classList.remove('is-pulsing');
            fillElement.removeEventListener('animationend', handleAnimationEnd);
          };
          fillElement.addEventListener('animationend', handleAnimationEnd);
        }

        const animate = (time) => {
          const elapsed = time - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easedProgress = ease(progress);

          currentPercent = startPercent + (targetPercent - startPercent) * easedProgress;
          this.style.setProperty('--percent', `${currentPercent}%`);

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            currentPercent = targetPercent;
            this.style.setProperty('--percent', `${currentPercent}%`);
          }
        };

        requestAnimationFrame(animate);
      }
    }

    customElements.define('free-shipping-progress-bar', FreeShippingProgressBar);
}

function updateAddonCardPricing() {
  const wrapper = document.querySelector('.cart-addons--inner[data-addon-discounts]');
  if (!wrapper) return;

  let discounts = {};
  try {
    const rawJson = wrapper.dataset.addonDiscounts;
    discounts = JSON.parse(rawJson || '{}');
  } catch (e) {
    console.warn('Failed to parse cart addon discounts JSON:', e);
  }

  const slides = document.querySelectorAll('.cart-addons--inner .swiper-slide[data-handle]');
  slides.forEach(slide => {
    const handle = slide.dataset.handle;
    const originalPrice = parseInt(slide.dataset.originalPrice, 10) || 0;
    const compareAtPrice = parseInt(slide.dataset.compareAtPrice, 10) || 0;
    const discountPct = discounts[handle] || 0;

    let finalPrice = originalPrice;
    let displayOriginal = null;

    if (discountPct > 0) {
      const multiplier = (100 - discountPct) / 100;
      finalPrice = Math.round(originalPrice * multiplier);
      displayOriginal = originalPrice;
    } else if (compareAtPrice > originalPrice) {
      finalPrice = originalPrice;
      displayOriginal = compareAtPrice;
    }

    const currencyFormat = window.theme.settings.money_with_currency_format || '${{amount}}';
    const priceElement = slide.querySelector('.product-card-sm__price');
    if (priceElement) {
      let priceHtml = '';
      if (displayOriginal) {
        const originalFormatted = formatMoney(displayOriginal, currencyFormat);
        const finalFormatted = formatMoney(finalPrice, currencyFormat);
        priceHtml = `<span class="text-xs" style="text-decoration: line-through; color: rgb(var(--color-text) / 0.6);">${originalFormatted}</span><span class="font-bold text-sm">${finalFormatted}</span>`;
      } else {
        const finalFormatted = formatMoney(finalPrice, currencyFormat);
        priceHtml = `<span class="font-bold text-sm">${finalFormatted}</span>`;
      }
      priceElement.innerHTML = priceHtml;
      priceElement.classList.remove('is-hidden');
    }

    slide.dataset.price = Math.round(finalPrice);
  });

  // Initialize/reinitialize Swiper carousel after price content changes
  const swiperEl = document.querySelector('#swiper-cart-addons');
  if (swiperEl) {
    if (swiperEl.swiper) {
      swiperEl.swiper.destroy(true, true);
    }
    new Swiper(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 10,
      loop: true,
      navigation: {
        nextEl: '.cart-addons--inner .swiper-button-next-custom',
        prevEl: '.cart-addons--inner .swiper-button-prev-custom',
      },
    });
  }
}

function updateSuggestionBadges() {
  const bar = document.querySelector('cart-drawer-items free-shipping-progress-bar');
  const isEligible = bar?.dataset.eligible === 'true';
  const slides = document.querySelectorAll('.cart-addons--inner .swiper-slide[data-price]');
  slides.forEach(slide => {
    const badge = slide.querySelector('.product-card-sm__unlock-badge');
    if (badge) {
      badge.textContent = isEligible ? '' : 'UNLOCKS FREE SHIPPING';
    }
  });
}

const CART_INSOLE_HANDLE = 'comfort-insoles-2-1';

// Resolve the insole variant that matches the sneaker already in the cart.
// [vrsn] 16 Oct 2026: matched by SKU, not by option name. This card sells the SNEAKER insole, so it only pairs
// with a sneaker line (MARK21-<colour>-M8 or MARK22-<colour>-M8 -> MARK21-INSOLE-M8). Duplicated offer products reuse the same SKUs.
// Returns null when the cart has no sneaker or there is no insole in that exact size; the caller then adds
// nothing (the old version fell back to the first insole, M4/W6, and also matched clogs).
async function resolveInsoleVariantForCart(insoleHandle) {
  const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const items = cart?.items || [];

  const sneaker = items.find((it) => /^MARK2[12]-/i.test(it.sku || '') && !/INSOLE/i.test(it.sku || ''));
  const sizeKey = sneaker && ((sneaker.sku || '').match(/-(M\d+)$/i) || [])[1];
  if (!sizeKey) return null;

  const insole = await fetch(`/products/${insoleHandle}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const wantedSku = `MARK21-INSOLE-${sizeKey.toUpperCase()}`;
  const match = (insole?.variants || []).find((v) => (v.sku || '').toUpperCase() === wantedSku);

  return match ? Number(match.id) : null;
}

function initAddonAddToCart() {
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('.cart-addon-form');
    if (!form) return;
    e.preventDefault();

    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton?.disabled) return;

    const cartDrawer = document.querySelector('cart-drawer');
    const addButtons = document.querySelectorAll('.cart-addon-form button[type="submit"]');
    const checkoutButton = document.querySelector('#CartDrawer-Checkout');

    addButtons.forEach((btn) => { btn.disabled = true; });
    if (checkoutButton) checkoutButton.disabled = true;
    submitButton?.querySelector('.loading__spinner')?.classList.remove('hidden');

    try {
      const formData = new FormData(form);

      // For the insole addon, add the variant matching the shoe size in the cart.
      let variantId = Number(formData.get('id'));
      const slideHandle = form.closest('.swiper-slide')?.dataset.handle;
      if (slideHandle === CART_INSOLE_HANDLE) {
        const matchedId = await resolveInsoleVariantForCart(CART_INSOLE_HANDLE);
        if (!matchedId) {
          // No sneaker in the cart, or no insole in its size: add nothing rather than a wrong size.
          addButtons.forEach((btn) => { btn.disabled = false; });
          if (checkoutButton) checkoutButton.disabled = false;
          submitButton?.querySelector('.loading__spinner')?.classList.add('hidden');
          return;
        }
        variantId = matchedId;
      }

      const sectionIds = cartDrawer?.getSectionsToRender?.().map((s) => s.id) || [];
      const payload = { items: [{ id: variantId, quantity: 1 }] };
      if (sectionIds.length) {
        payload.sections = sectionIds;
        payload.sections_url = window.location.pathname;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then((r) => r.json());

      if (response.status) {
        console.error('[CartAddon] add to cart failed:', response.description || response.message);
        addButtons.forEach((btn) => { btn.disabled = false; });
        if (checkoutButton) checkoutButton.disabled = false;
        submitButton?.querySelector('.loading__spinner')?.classList.add('hidden');
        return;
      }

      if (cartDrawer?.renderContents) cartDrawer.renderContents(response);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-addon-form', cartData: response });
      }
    } catch (err) {
      console.error('[CartAddon] add to cart error:', err);
      addButtons.forEach((btn) => { btn.disabled = false; });
      if (checkoutButton) checkoutButton.disabled = false;
      submitButton?.querySelector('.loading__spinner')?.classList.add('hidden');
    }
  });
}

// Insole pairing check: the note under a mismatched insole line (snippets/cart-insole-check.liquid)
// has a swap button. Add the fitting insole first, then remove the wrong line, so a failed add changes nothing.
function initInsoleSwap() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-insole-swap]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;
    btn.style.opacity = '0.6';

    const cartDrawer = btn.closest('cart-drawer');
    const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
    let added = false;

    try {
      // Liquid prints line properties as [[key, value], ...] ([] when empty); /cart/add.js wants an object.
      let properties = {};
      try {
        const parsed = JSON.parse(btn.dataset.properties || '{}');
        if (Array.isArray(parsed)) properties = Object.fromEntries(parsed.filter((p) => Array.isArray(p) && p.length === 2));
        else if (parsed && typeof parsed === 'object') properties = parsed;
      } catch (_) {}
      if (properties._gift_for_variant && btn.dataset.shoeVariant) properties._gift_for_variant = btn.dataset.shoeVariant;

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: json,
        body: JSON.stringify({ items: [{ id: Number(btn.dataset.variantId), quantity: Number(btn.dataset.quantity) || 1, properties }] }),
      }).then((r) => r.json());
      if (addResponse.status) throw new Error(addResponse.description || addResponse.message);
      added = true;

      const sectionIds = cartDrawer?.getSectionsToRender?.().map((s) => s.id) || [];
      const body = { id: btn.dataset.lineKey, quantity: 0 };
      if (sectionIds.length) {
        body.sections = sectionIds;
        body.sections_url = window.location.pathname;
      }
      const response = await fetch('/cart/change.js', { method: 'POST', headers: json, body: JSON.stringify(body) }).then((r) => r.json());
      if (response.status) throw new Error(response.description || response.message);

      if (!cartDrawer?.renderContents) {
        window.location.reload();
        return;
      }
      cartDrawer.renderContents(response);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'insole-swap', cartData: response });
      }
    } catch (err) {
      console.error('[InsoleSwap] swap failed:', err);
      // The cart changed only if the add went through: reload to show it; otherwise let the shopper try again.
      if (added) {
        window.location.reload();
        return;
      }
      btn.disabled = false;
      btn.style.opacity = '';
    }
  });
}

initAddonAddToCart();
initInsoleSwap();
updateAddonCardPricing();